import { Activity, Sun, Wind, Footprints, ArrowUpRight } from "lucide-react";
import { useBooking } from "@/components/site/BookingProvider";

const SERVICES = [
  {
    id: "pemf",
    eyebrow: "01 — Cellular activation",
    title: "Pulse XL PEMF",
    brand: "Pulse Centers · Certified",
    desc:
      "Two clinical-grade Pulse XL Pro chairs deliver electromagnetic pulses that stimulate ATP production, oxygenation, and natural repair. 15- or 30-minute sessions with full control over frequency and intensity.",
    bullets: [
      "Two Pulse XL Pro stations",
      "Customizable PPS & MFS settings",
      "Full-body or targeted protocols",
    ],
    icon: Activity,
    image:
      "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/UL9GAEU0ONW7drLQGkjG/media/6859b2690bf945f143138640.png",
    span: "lg:col-span-7",
  },
  {
    id: "redlight",
    eyebrow: "02 — Light medicine",
    title: "PlatinumLED Booth",
    brand: "PlatinumLED Therapy Lights",
    desc:
      "A private red-light booth with four PlatinumLED panels on each side — eight medical-grade panels delivering full-spectrum red and near-infrared light in 15-minute sessions, 8am–5pm.",
    bullets: [
      "8 PlatinumLED panels (4 per side)",
      "FDA-cleared, Class II",
      "Painless · 15 min",
    ],
    icon: Sun,
    image:
      "https://platinumtherapylights.com/cdn/shop/files/download_253d1deb-f692-4327-b610-ea2a7a883f8c.webp?v=1774922363&width=800",
    span: "lg:col-span-5",
  },
  {
    id: "hbot",
    eyebrow: "03 — Pressurized oxygen",
    title: "Oxygen Health Systems HBOT",
    brand: "Oxygen Health Systems",
    desc:
      "Five sessions a day in a true Oxygen Health Systems chamber — the same clinical-grade platform used in research. Pure oxygen at increased pressure floods the bloodstream, supporting brain, recovery, and stem-cell activity.",
    bullets: [
      "Oxygen saturation boost",
      "Wound healing & inflammation",
      "60 or 90-minute protocols",
    ],
    icon: Wind,
    image:
      "https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/UL9GAEU0ONW7drLQGkjG/media/6882c41d53c844291bd46574.jpeg",
    span: "lg:col-span-5",
  },
  {
    id: "recovery",
    eyebrow: "04 — Massage & recovery zone",
    title: "Infinity Chair + Recovery Floor",
    brand: "Infinity · Therabody · Human Touch",
    desc:
      "An Infinity 3D massage chair, Therabody RecoveryAir compression boots, the Human Touch Reflex SOL foot massager, foam rollers, acupressure mats — and a quiet bar of herbal teas of the day.",
    bullets: [
      "Infinity 3D massage chair",
      "Therabody compression boots",
      "Reflex SOL foot massage · herbal teas",
    ],
    icon: Footprints,
    image:
      "https://res.cloudinary.com/infinity-massage-chairs/image/upload/f_auto,q_auto:best/v1756820659/600_x_600-3Q-Right_kwkfve.png",
    span: "lg:col-span-7",
  },
];

export default function Services() {
  return (
    <section
      id="technology"
      data-testid="services-section"
      className="relative bg-bone py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 sm:mb-20 mll-reveal">
          <div className="lg:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay">
              The Technology
            </p>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-serif font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-stone leading-[1.05]">
              Four modalities,{" "}
              <em className="italic font-normal text-jungle">
                one quiet protocol
              </em>{" "}
              for the body.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-muted-ink max-w-2xl leading-relaxed">
              Every device at Maui Longevity Lounge is FDA-cleared and chosen
              for one reason — measurable outcomes you can feel within a
              single visit. Pulse, PlatinumLED, Oxygen Health Systems, and
              Infinity — under one roof in Haiku.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mll-stagger">
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} {...s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ id, eyebrow, title, brand, desc, bullets, icon: Icon, image, span }) {
  const { openBooking } = useBooking();
  return (
    <article
      id={id}
      data-testid={`service-card-${id}`}
      className={`group ${span} bg-white border border-sand overflow-hidden flex flex-col`}
    >
      <div className="mll-zoom relative aspect-[16/10] w-full overflow-hidden bg-sand">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-bone/90 backdrop-blur px-3 py-1.5">
          <Icon className="w-3.5 h-3.5 text-jungle" />
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-stone">
            {eyebrow}
          </span>
        </div>
      </div>

      <div className="p-7 sm:p-9 flex-1 flex flex-col">
        <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-clay">
          {brand}
        </p>
        <h3 className="mt-2 font-serif text-2xl sm:text-3xl text-stone tracking-tight">
          {title}
        </h3>
        <p className="mt-4 text-[15px] leading-relaxed text-muted-ink">
          {desc}
        </p>

        <ul className="mt-6 space-y-2.5">
          {bullets.map((b) => (
            <li
              key={b}
              className="flex items-start gap-3 text-sm text-stone/85"
            >
              <span className="mt-2 w-1 h-1 rounded-full bg-clay shrink-0" />
              {b}
            </li>
          ))}
        </ul>

        <div className="mt-8 pt-6 border-t border-sand">
          <button
            type="button"
            onClick={openBooking}
            data-testid={`reserve-${id}`}
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] font-semibold text-jungle group-hover:text-clay transition-colors"
          >
            Reserve this modality
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </article>
  );
}
